/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useMemo } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { frame } from '../flight/state.ts'
import { TUBES, RINGS } from './tubesData.ts'
import { glsl, SURFACE2, SURFACE3, CYAN, CYAN_CORE, CYAN_LIGHT, CYAN_WASH } from '../palette.ts'

// FOUNDATION §6.4 — three registers plus rings, five draw calls. Cyan reads as
// emissive on white via §4.2: darker, MORE saturated at the core (core/halo
// inversion), a BackSide halo shell for the diffusion — normal blending, no
// additive, no bloom, ever. The floor and desks carry the spill.

const SURF_VERT = /* glsl */ `
attribute float aEnd;
uniform float uFog;
uniform vec3 uBg, uColor;
varying vec3 vCol;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mv;
  vec3 n = normalize(normalMatrix * normal);
  float l = 0.72 + 0.28 * max(dot(n, normalize(vec3(0.35, 1.0, 0.5))), 0.0);
  vec3 col = uColor * l;
  float dist = length(mv.xyz);
  float fo = 1.0 - exp(-uFog * uFog * dist * dist);
  float lum = dot(col, vec3(0.299, 0.587, 0.114));
  col = mix(col, vec3(lum), clamp(fo * 1.7, 0.0, 1.0));
  col = mix(col, uBg, fo);
  // the taper: the run dissolves into the air before its geometry ends —
  // no visible cut, the pipe just goes "to infinity"
  vCol = mix(uBg, col, aEnd);
}
`
const SURF_FRAG = /* glsl */ `
varying vec3 vCol;
void main() { gl_FragColor = vec4(vCol, 1.0); }
`

// Core/halo inversion: deep saturated core where the run faces you, lighter at
// the silhouette. Chroma-kill is fog-relative: distant cyan greys before it pales.
const LIGHT_VERT = /* glsl */ `
attribute float aEnd;
uniform float uFog, uChroma, uTime;
uniform vec3 uBg;
varying vec3 vCol;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mv;
  vec3 n = normalize(normalMatrix * normal);
  vec3 v = normalize(-mv.xyz);
  float facing = abs(dot(n, v));
  float pulse = 0.85 + 0.15 * sin(uTime * 2.0 + position.z * 0.6 + position.x * 0.3);
  vec3 core = mix(${glsl(CYAN_CORE)}, ${glsl(CYAN)}, 0.5 + 0.3 * sin(position.z * 0.4));
  vec3 lit = mix(${glsl(CYAN_LIGHT)}, core, smoothstep(0.25, 0.95, facing)) * pulse;
  vec3 off = ${glsl(SURFACE3)};
  vec3 col = mix(off, lit, smoothstep(0.0, 0.45, uChroma));
  float dist = length(mv.xyz);
  float fo = 1.0 - exp(-uFog * uFog * dist * dist);
  float grey = clamp((dist * uFog - 0.55) / 0.5, 0.0, 0.8);
  col = mix(col, vec3(dot(col, vec3(0.299, 0.587, 0.114))), grey);
  col = mix(col, uBg, fo);
  vCol = mix(uBg, col, aEnd);
}
`

// The halo shell: a fatter BackSide tube, alpha by facing — light diffusing into
// the air around the run, with normal blending only.
const HALO_VERT = /* glsl */ `
attribute float aEnd;
uniform float uFog, uChroma;
uniform vec3 uBg;
varying vec3 vCol;
varying float vA;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mv;
  vec3 n = normalize(normalMatrix * normal);
  vec3 v = normalize(-mv.xyz);
  float facing = abs(dot(n, v));
  float dist = length(mv.xyz);
  float fo = 1.0 - exp(-uFog * uFog * dist * dist);
  vec3 col = mix(${glsl(CYAN_WASH)}, ${glsl(CYAN_LIGHT)}, facing * 0.6);
  vCol = mix(col, uBg, fo);
  vA = pow(facing, 1.6) * 0.5 * smoothstep(0.0, 0.45, uChroma) * (1.0 - fo) * aEnd;
}
`
const HALO_FRAG = /* glsl */ `
varying vec3 vCol;
varying float vA;
void main() { gl_FragColor = vec4(vCol, vA); }
`

const GLASS_VERT = /* glsl */ `
attribute float aEnd;
uniform float uFog;
uniform vec3 uBg;
varying vec3 vCol;
varying float vA;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mv;
  vec3 n = normalize(normalMatrix * normal);
  vec3 v = normalize(-mv.xyz);
  float fr = 1.0 - abs(dot(n, v));
  float dist = length(mv.xyz);
  float fo = 1.0 - exp(-uFog * uFog * dist * dist);
  vec3 col = mix(uBg * 0.97, vec3(0.62, 0.69, 0.73), pow(fr, 1.6));
  vCol = mix(col, uBg, fo);
  // more transparent at the faces than before, so the captured glyph streams
  // read THROUGH the glass (the translucent pipe-flow was V1's best moment)
  vA = mix(0.07, 0.42, pow(fr, 1.6)) * (1.0 - fo) * aEnd;
}
`

const smoothstep = (a: number, b: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)))
  return t * t * (3 - 2 * t)
}

// The port from V1's plumbing read: no pipe may end in a visible cut. Each run's
// cross-section rings shrink toward the ends (the pipe thins into a spike) and an
// aEnd attribute lets every shader dissolve the last stretch into the air colour —
// the run reads as continuing to infinity, swallowed by aerial perspective.
const SEGS = 56
const RADIAL = 10
function taperedTube(
  t: { curve: THREE.CatmullRomCurve3; radius: number },
  radiusScale: number,
  taperStart: boolean,
  taperEnd: boolean,
): THREE.BufferGeometry {
  const g = new THREE.TubeGeometry(t.curve, SEGS, t.radius * radiusScale, RADIAL)
  const pos = g.attributes.position
  const aEnd = new Float32Array(pos.count)
  const c = new THREE.Vector3()
  const p = new THREE.Vector3()
  const perRing = RADIAL + 1
  for (let i = 0; i <= SEGS; i++) {
    const u = i / SEGS
    const d = Math.min(taperStart ? u : 1, taperEnd ? 1 - u : 1)
    const scale = 0.06 + 0.94 * smoothstep(0, 0.22, d)
    const fade = smoothstep(0, 0.13, d)
    c.set(0, 0, 0)
    for (let k = 0; k < perRing; k++) c.add(p.fromBufferAttribute(pos, i * perRing + k))
    c.multiplyScalar(1 / perRing)
    for (let k = 0; k < perRing; k++) {
      const idx = i * perRing + k
      p.fromBufferAttribute(pos, idx).sub(c).multiplyScalar(scale).add(c)
      pos.setXYZ(idx, p.x, p.y, p.z)
      aEnd[idx] = fade
    }
  }
  g.setAttribute('aEnd', new THREE.BufferAttribute(aEnd, 1))
  return g
}

export function Tubes() {
  const built = useMemo(() => {
    const byRegister = (r: number, radiusScale = 1) =>
      mergeGeometries(
        TUBES.filter((t) => t.register === r).map((t) =>
          // glass conduits keep their intake mouths crisp — a mouth is an
          // intentional opening; only arbitrary terminations taper
          taperedTube(t, radiusScale, r !== 1, true)
        ),
      )!
    const duct = byRegister(0)
    const glass = byRegister(1)
    const light = byRegister(2)
    const halo = byRegister(2, 3.4)

    // rings are static and few — bake them into one merged geometry (the surface
    // shader has no per-instance path; see ISSUES.md #8 for the instancing rule)
    const ringGeo = mergeGeometries(
      RINGS.map((r) => {
        const g = new THREE.TorusGeometry(1.16, 0.09, 6, 16)
        g.applyMatrix4(r.m)
        return g
      }),
    )!
    // rings never taper — but they share SURF_VERT, so aEnd must exist (a missing
    // attribute reads as 0 and the rings would dissolve entirely)
    ringGeo.setAttribute(
      'aEnd',
      new THREE.BufferAttribute(new Float32Array(ringGeo.attributes.position.count).fill(1), 1),
    )
    const ringMat = new THREE.ShaderMaterial({
      vertexShader: SURF_VERT,
      fragmentShader: SURF_FRAG,
      uniforms: {
        uFog: { value: 0.016 },
        uBg: { value: new THREE.Color() },
        uColor: { value: SURFACE3.clone() },
      },
    })

    const ductMat = new THREE.ShaderMaterial({
      vertexShader: SURF_VERT,
      fragmentShader: SURF_FRAG,
      uniforms: {
        uFog: { value: 0.016 },
        uBg: { value: new THREE.Color() },
        uColor: { value: SURFACE2.clone() },
      },
    })
    const lightMat = new THREE.ShaderMaterial({
      vertexShader: LIGHT_VERT,
      fragmentShader: SURF_FRAG,
      uniforms: {
        uFog: { value: 0.016 },
        uChroma: { value: 0 },
        uTime: { value: 0 },
        uBg: { value: new THREE.Color() },
      },
    })
    const haloMat = new THREE.ShaderMaterial({
      vertexShader: HALO_VERT,
      fragmentShader: HALO_FRAG,
      uniforms: {
        uFog: { value: 0.016 },
        uChroma: { value: 0 },
        uBg: { value: new THREE.Color() },
      },
      transparent: true,
      depthWrite: false,
      side: THREE.BackSide,
    })
    const glassMat = new THREE.ShaderMaterial({
      vertexShader: GLASS_VERT,
      fragmentShader: HALO_FRAG,
      uniforms: {
        uFog: { value: 0.016 },
        uBg: { value: new THREE.Color() },
      },
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
    return { duct, glass, light, halo, ringGeo, ringMat, ductMat, lightMat, haloMat, glassMat }
  }, [])

  useFrame((st) => {
    const b = frame.beats
    const mats = [built.ductMat, built.lightMat, built.haloMat, built.glassMat, built.ringMat]
    for (const m of mats) {
      m.uniforms.uFog.value = b.fog
      ;(m.uniforms.uBg.value as THREE.Color).copy(frame.bg)
      if (m.uniforms.uChroma) m.uniforms.uChroma.value = b.chroma
      if (m.uniforms.uTime) m.uniforms.uTime.value = st.clock.elapsedTime
    }
  })

  return (
    <>
      <mesh geometry={built.duct} material={built.ductMat} />
      <mesh geometry={built.ringGeo} material={built.ringMat} />
      <mesh geometry={built.glass} material={built.glassMat} renderOrder={4} />
      <mesh geometry={built.light} material={built.lightMat} renderOrder={2} />
      <mesh geometry={built.halo} material={built.haloMat} renderOrder={3} />
    </>
  )
}
